import { useRef, useState } from 'react';
import { fShort, inr, span, type DocRef, type Expense } from '@nandanam/domain';
import { useStore } from '../app/store';
import { Chips, Field, Modal, MoneyInput } from '../ui/components';
import { Icon } from '../ui/icons';
import { MAX_UPLOAD_BYTES, fmtSize } from '../app/files';
import { ErrorBox, ModeChips, ViewOnlyNote, todayISO, useSubmit } from './kit';

/** Add or edit an expense. Expenses are voided with a reason, never deleted. */
export function ExpenseOverlay({ id: editId, booking: forBooking }: { id?: string; booking?: string }) {
  const S = useStore(), s = S.state.settings;
  const ex: Expense | undefined = editId ? S.state.expenses.find(x => x.id === editId) : undefined;
  const [date, setDate] = useState(ex?.date ?? todayISO(S));
  const [cat, setCat] = useState(ex?.cat ?? s.expenseCats[0]!);
  const [amount, setAmount] = useState<number | ''>(ex?.amount ?? '');
  const [desc, setDesc] = useState(ex?.desc ?? '');
  const [vendor, setVendor] = useState(ex?.vendor ?? '');
  const [booking, setBooking] = useState(ex?.booking ?? forBooking ?? '');
  const [mode, setMode] = useState(ex?.mode ?? s.payModes[0]!);
  const [notes, setNotes] = useState(ex?.notes ?? '');
  const [receipt, setReceipt] = useState<DocRef | null>(ex?.receipt ?? null);
  const [uploading, setUploading] = useState(false);
  const [newId] = useState(() => S.newId());
  const file = useRef<HTMLInputElement>(null);
  const sub = useSubmit();
  const recent = S.state.records.filter(r => ['upcoming', 'inprogress', 'closing', 'completed'].includes(S.phase(r))).sort((a, b) => span(b).start.localeCompare(span(a).start)).slice(0, 30);
  const problem = !amount ? 'Enter an amount' : !desc.trim() ? 'Add a short description' : null;

  const onFile = async (f?: File) => {
    if (!f) return;
    if (f.size > MAX_UPLOAD_BYTES) { S.toast('That file is over 20 MB'); return; }
    setUploading(true);
    try { setReceipt(await S.files.put(f, { id: S.newId(), folder: 'receipts', cat: 'Receipt', date })); }
    catch { S.toast('Could not attach the receipt'); }
    finally { setUploading(false); }
  };
  const fields = { date, cat, amount, desc: desc.trim(), vendor: vendor.trim(), booking: booking || null, mode, notes, receipt };
  const save = () => sub.run(async () => {
    const x = ex ? await S.act('expense.update', `expense:${ex.id}`, { id: ex.id, patch: fields }, 'Expense updated')
      : await S.act('expense.add', `expense:${newId}`, { id: newId, ...fields }, `${inr(Number(amount))} expense added`);
    if (x.ok) S.close();
    return x;
  });
  return (
    <Modal size="lg" kicker="Expenses" title={ex ? 'Edit expense' : 'Add expense'} onClose={() => S.close()}
      footer={<><button className="btn" onClick={() => S.close()}>Cancel</button><button className="btn primary" disabled={!!problem || sub.busy || uploading || !S.canEdit} onClick={save}>{sub.busy ? 'Saving…' : ex ? 'Save changes' : 'Add expense'}</button></>}>
      <ViewOnlyNote />
      <div className="grid2">
        <Field label="Amount"><MoneyInput value={amount} onChange={setAmount} /></Field>
        <Field label="Date"><input type="date" className="input" value={date} onChange={e => setDate(e.target.value)} /></Field>
      </div>
      <Field label="Category"><Chips value={cat} options={s.expenseCats.map(c => ({ v: c, label: c }))} onChange={setCat} /></Field>
      <div className="grid2">
        <Field label="Description"><input className="input" placeholder="e.g. Pool chemicals" value={desc} onChange={e => setDesc(e.target.value)} /></Field>
        <Field label="Vendor"><input className="input" placeholder="Optional" value={vendor} onChange={e => setVendor(e.target.value)} /></Field>
      </div>
      <div className="grid2">
        <Field label="Linked booking"><select className="select" value={booking} onChange={e => setBooking(e.target.value)}><option value="">None</option>{recent.map(r => { const sp = span(r); return <option key={r.id} value={r.id}>{S.num(r)} · {S.cust(r.cust).name} · {fShort(sp.start, sp.end)}</option>; })}</select></Field>
        <Field label="Paid by"><ModeChips value={mode} onChange={setMode} /></Field>
      </div>
      <Field label="Notes"><textarea className="textarea" rows={2} value={notes} onChange={e => setNotes(e.target.value)} /></Field>
      <div className="field"><span className="lbl">Receipt</span>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {receipt ? <span className="doc" style={{ flex: '0 1 auto' }}><Icon name={/^image\//.test(receipt.mime) ? 'image' : 'file'} size={18} /><span className="ellipsis" style={{ maxWidth: 220 }}>{receipt.name}</span><span className="muted" style={{ fontSize: 12 }}>{fmtSize(receipt.size)}</span></span> : <span className="muted" style={{ fontSize: 13 }}>No receipt attached</span>}
          <button type="button" className="btn small" disabled={uploading || !S.canEdit} onClick={() => file.current?.click()}><Icon name="camera" size={14} />{uploading ? 'Attaching…' : receipt ? 'Replace' : 'Attach photo or PDF'}</button>
          {receipt && <button type="button" className="btn small ghost" onClick={() => setReceipt(null)}>Remove</button>}
          <input ref={file} type="file" accept="image/*,application/pdf" capture="environment" hidden onChange={e => { void onFile(e.target.files?.[0]); e.target.value = ''; }} />
        </div>
      </div>
      <ErrorBox error={sub.error} />
    </Modal>
  );
}

export function VoidExpenseOverlay({ id, investment }: { id: string; investment?: boolean }) {
  const S = useStore();
  const x = investment ? S.state.investments.find(e => e.id === id) : S.state.expenses.find(e => e.id === id);
  const [reason, setReason] = useState('');
  const sub = useSubmit();
  if (!x) return null;
  const what = investment ? 'investment' : 'expense';
  const save = () => sub.run(async () => { const r = await S.act(`${what}.void`, `${what}:${id}`, { id, reason: reason.trim(), at: S.nowLocal() }, `${investment ? 'Investment' : 'Expense'} voided`); if (r.ok) S.close(); return r; });
  return (
    <Modal kicker={investment ? 'Investments' : 'Expenses'} title={`Void this ${what}?`} sub={`${x.desc} · ${inr(x.amount)}`} onClose={() => S.close()}
      footer={<><button className="btn" onClick={() => S.close()}>Keep it</button><button className="btn danger" disabled={!reason.trim() || sub.busy || !S.canEdit} onClick={save}>{sub.busy ? 'Saving…' : 'Void'}</button></>}>
      <div style={{ fontSize: 14 }}>It stays in the list, crossed out with your reason, and stops counting in reports.</div>
      <Field label="Reason"><input className="input" placeholder="e.g. Entered twice" value={reason} onChange={e => setReason(e.target.value)} /></Field>
      <ErrorBox error={sub.error} />
    </Modal>
  );
}

/** Capital spend (the mobile prototype's entry form). Counted separately from running expenses. */
export function InvestmentOverlay() {
  const S = useStore(), s = S.state.settings;
  const [date, setDate] = useState(todayISO(S));
  const [cat, setCat] = useState(s.investmentCats[0]!);
  const [amount, setAmount] = useState<number | ''>('');
  const [desc, setDesc] = useState('');
  const [vendor, setVendor] = useState('');
  const [notes, setNotes] = useState('');
  const [id] = useState(() => S.newId());
  const sub = useSubmit();
  const save = () => sub.run(async () => { const x = await S.act('investment.add', `investment:${id}`, { id, date, cat, amount, desc: desc.trim(), vendor: vendor.trim(), notes }, `${inr(Number(amount))} investment added`); if (x.ok) S.close(); return x; });
  return (
    <Modal kicker="Stats" title="Add investment" sub="One-off spend on the property (lighting, furniture, pool…). Kept apart from monthly expenses." onClose={() => S.close()}
      footer={<><button className="btn" onClick={() => S.close()}>Cancel</button><button className="btn primary" disabled={!amount || !desc.trim() || sub.busy || !S.canEdit} onClick={save}>{sub.busy ? 'Saving…' : 'Add investment'}</button></>}>
      <ViewOnlyNote />
      <div className="grid2">
        <Field label="Amount"><MoneyInput value={amount} onChange={setAmount} /></Field>
        <Field label="Date"><input type="date" className="input" value={date} onChange={e => setDate(e.target.value)} /></Field>
      </div>
      <Field label="Category"><Chips value={cat} options={s.investmentCats.map(c => ({ v: c, label: c }))} onChange={setCat} /></Field>
      <div className="grid2">
        <Field label="Description"><input className="input" placeholder="e.g. Garden lights" value={desc} onChange={e => setDesc(e.target.value)} /></Field>
        <Field label="Vendor"><input className="input" placeholder="Optional" value={vendor} onChange={e => setVendor(e.target.value)} /></Field>
      </div>
      <Field label="Notes"><textarea className="textarea" rows={2} value={notes} onChange={e => setNotes(e.target.value)} /></Field>
      <ErrorBox error={sub.error} />
    </Modal>
  );
}
